import { IconV2FolderOutline } from '@icons/IconV2FolderOutline'
import { ENDPOINTS } from './util/endpoints'
import { IconV2BuildingOutline } from '@icons/IconV2BuildingOutline'
import { IconV2ComputerOutline } from '@icons/IconV2ComputerOutline'
import { IconV2FolderDocumentsOutline } from '@icons/IconV2FolderDocumentsOutline'
import { IconV2MeetingRoomOutline } from '@icons/IconV2MeetingRoomOutline'
import { IconV2DocumentDraftOutline } from '@icons/IconV2DocumentDraftOutline'
import { IconV2RobotOutline } from '@icons/IconV2RobotOutline'
import { IconServiceOutline } from '@icons/IconServiceOutline'
import { IconV2ThoughtOutline } from '@icons/IconV2ThoughtOutline'

export const PWC_ID_SCOPES = [
  'openid',
  'address',
  'aduidscope',
  'businessunit',
  'cloudEmail',
  'costcenter',
  'country',
  'countrycode',
  'email',
  'employeeNumber',
  'employeetype',
  'GlobalGradeCode',
  'groups',
  'isMemberOf',
  'jobtitle',
  'locality',
  'LOS',
  'organization',
  'phone',
  'phone_number',
  'postalcode',
  'preferredLanguage',
  'preferredMail',
  'profile',
  'PwCassignedofficecode',
  'pwcGlobalGradeAbbreviatedName',
  'PwClos',
  'pwcPartyID',
  'PwCPPI',
  'PwCStaffClass',
  'pwcWorkerID',
  'role',
  'splitgroups',
  'staffnumber',
  'stateorprovince',
  'street',
  'uid',
  'upwcjobtitle',
  'windowsaccountname',
]

// Base identity provider config
export interface IdentityProviderConfig {
  readonly host: string
  readonly providerUrl: string
  readonly clientId: string
  readonly redirectUrl: string
  // Issuer the IdP will echo back in the redirect's `iss` query param.
  // Used by the front-end as a fallback when the IdP omits the param.
  readonly issuer: string
  readonly debug: boolean
  readonly production: boolean
  authUrl(postLoginRedirectUrl?: string): string
  redirectBrowserToAuthUrl(postLoginRedirectUrl?: string): void
}

export class PwCIDConfig implements IdentityProviderConfig {
  readonly redirectUrl: string
  readonly scopes: string[]
  readonly scopesJoined: string
  readonly issuer = 'https://login.pwc.com/openam/oauth2'

  constructor(
    readonly host: string,
    readonly providerUrl: string,
    readonly clientId: string,
    readonly debug: boolean = false,
    readonly production: boolean = false // readonly redirectUrl: string = `${host}/oauth`,
  ) {
    this.redirectUrl = `${host}/oauth`
    this.scopes = PWC_ID_SCOPES
    this.scopesJoined = this.scopes.join(' ')
  }

  authUrl(postLoginRedirectUrl?: string | undefined): string {
    const state = postLoginRedirectUrl // `state` input parameter is preserved in authentication response
    return (
      `${this.providerUrl}?` +
      `response_type=code` +
      `&scope=${this.scopesJoined}` +
      `&client_id=${this.clientId}` +
      `&redirect_uri=${this.redirectUrl}` +
      (state! ? `&state=${state}` : '')
    )
  }

  redirectBrowserToAuthUrl(postLoginRedirectUrl?: string | undefined): void {
    window.location.href = this.authUrl(postLoginRedirectUrl)
  }

  static forHost(host: string): IdentityProviderConfig {
    switch (host) {
      // ----- Microsoft Entra hosts -----
      case 'https://localhost:9080':
      case 'https://uat.za.datamodellingplatform.africa.pwcglb.com':
      case 'https://uat.za.datamodellingplatform.africa.pwc.com':
        return new EntraIDConfig(
          host,
          '513294a0-3e20-41b2-a970-6d30bf1546fa',
          '9372cdf7-3f4e-473f-be0b-f1515ede906b',
          true,
          false
        )
      case 'https://datamodellingplatform.za.pwcglb.com':
      case 'https://datamodellingplatform.za.pwc.com':
        return new EntraIDConfig(
          host,
          '513294a0-3e20-41b2-a970-6d30bf1546fa',
          'e99ce372-f8e4-4cbf-b5b7-f84fa22613e2',
          false,
          true
        )
      case 'https://eu.datamodellingplatform.africa.pwc.com':
      case 'https://eu.datamodellingplatform.africa.pwcglb.com':
      case 'https://dr.eu.datamodellingplatform.africa.pwcglb.com':
      case 'https://www.datamodelling.pwc.com.au':
      case 'https://dev-gcp.datamodellingplatform.za.pwc.com':
      case 'https://uat-gcp.datamodellingplatform.sa.pwc.com':
        return new EntraIDConfig(
          host,
          '513294a0-3e20-41b2-a970-6d30bf1546fa',
          'b979a0a2-df71-41db-9a17-d6a43b76c7a4',
          false,
          true
        )
      default:
        console.warn('using fallback openid config', host)
        return new EntraIDConfig(
          host,
          '513294a0-3e20-41b2-a970-6d30bf1546fa',
          '9372cdf7-3f4e-473f-be0b-f1515ede906b',
          true,
          false
        )
    }
  }
}

// ----- Microsoft Entra (Azure AD) -----
export class EntraIDConfig implements IdentityProviderConfig {
  readonly redirectUrl: string
  readonly providerUrl: string
  readonly issuer: string
  // Request offline access so the token response includes a refresh token.
  // Entra v2.0 has no implicit defaults - omitting `scope` means no id_token.
  readonly scopes = ['openid', 'profile', 'email', 'offline_access']
  readonly scopesJoined = this.scopes.join(' ')

  constructor(
    readonly host: string,
    readonly tenantId: string,
    readonly clientId: string,
    readonly debug: boolean = false,
    readonly production: boolean = false
  ) {
    this.redirectUrl = `${host}/oauth`
    this.providerUrl = `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/authorize`
    this.issuer = `https://login.microsoftonline.com/${tenantId}/v2.0`
  }

  authUrl(postLoginRedirectUrl?: string | undefined): string {
    // `state` is opaque and Entra round-trips it; we use it for the post-login redirect path.
    const state = postLoginRedirectUrl
    const params = new URLSearchParams({
      client_id: this.clientId,
      response_type: 'code',
      response_mode: 'query',
      redirect_uri: this.redirectUrl,
      scope: this.scopesJoined,
    })
    if (state) params.set('state', state)
    return `${this.providerUrl}?${params.toString()}`
  }

  redirectBrowserToAuthUrl(postLoginRedirectUrl?: string | undefined): void {
    window.location.href = this.authUrl(postLoginRedirectUrl)
  }
}

export const CATALOG_BASE_PATH = '/apps'

const host = window.location.origin
// PWC_ID_CONFIG keeps its name for compatibility but may be either provider now.
export const PWC_ID_CONFIG: IdentityProviderConfig = PwCIDConfig.forHost(host)
export const IMG_BASE_PATH = `${ENDPOINTS.DOTNET}/rest/support/getImage?imageKey=`

export const PRESENCE_ANIMATION = {
  initial: { opacity: 0, y: 5 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: 5 },
  transition: {
    animate: { duration: 0.2, ease: 'easeOut' },
    exit: { duration: 0.05, ease: 'easeOut' },
    layout: { duration: 0.2, ease: 'easeOut' },
  },
} as const

export const ProjectIcon = IconV2MeetingRoomOutline
export const OrganisationIcon = IconV2BuildingOutline
export const ApplicationIcon = IconV2ComputerOutline
export const WorkspaceIcon = IconV2FolderDocumentsOutline
export const SkillsIcon = IconV2DocumentDraftOutline
export const ToolsIcon = IconServiceOutline
export const ReasoningIcon = IconV2ThoughtOutline
export const AgentIcon = IconV2RobotOutline
